import React, {useState} from 'react';
import {Alert, Button, Badge} from 'reactstrap';
import {ChevronDown, ChevronRight, ShieldAlert} from 'lucide-react';
import {ApprovalRequestItem} from '../../api/types';

const formatArguments = (argumentsJson: string) => {
    try {
        return JSON.stringify(JSON.parse(argumentsJson), null, 2);
    } catch {
        return argumentsJson;
    }
};

interface ApprovalRequestCardProps {
    item: ApprovalRequestItem;
    /** Pending only while this item is the run's awaited approval. */
    isPending: boolean;
    isSubmitting: boolean;
    onDecision: (approved: boolean) => void;
}

/**
 * The run pauses on this card: the model wants to call a tool flagged as requiring
 * confirmation. The headline is the tool's friendly description when it provides one;
 * the technical details (tool name + raw arguments) sit behind a collapsed toggle.
 */
const ApprovalRequestCard: React.FC<ApprovalRequestCardProps> = ({
    item,
    isPending,
    isSubmitting,
    onDecision
}) => {
    const [showDetails, setShowDetails] = useState(false);

    const toolLabel = item.serverLabel
        ? `${item.serverLabel} / ${item.toolName}`
        : item.toolName;

    return (
        <Alert color="warning" className="mb-2 py-2 px-3">
            <div className="d-flex align-items-center gap-2 small fw-bold">
                <ShieldAlert size={16} />
                Approval required
                {!isPending && (
                    <Badge
                        color={item.status === 'failed' ? 'secondary' : 'light'}
                        pill
                    >
                        {item.status === 'failed' ? 'expired' : 'answered'}
                    </Badge>
                )}
            </div>

            <div className="small mt-1">
                {item.description ? (
                    item.description
                ) : (
                    <>
                        The assistant wants to call <code>{toolLabel}</code>
                        {item.serverName ? ` on ${item.serverName}` : ''}.
                    </>
                )}
            </div>

            <button
                type="button"
                className="btn btn-link btn-sm p-0 text-muted text-decoration-none d-flex align-items-center gap-1 small mt-1"
                onClick={() => setShowDetails(value => !value)}
            >
                {showDetails ? (
                    <ChevronDown size={12} />
                ) : (
                    <ChevronRight size={12} />
                )}
                Details
            </button>
            {showDetails && (
                <div className="small mt-1">
                    <div>
                        Tool: <code>{toolLabel}</code>
                        {item.serverName ? ` on ${item.serverName}` : ''}
                    </div>
                    <pre
                        className="bg-white border rounded p-2 my-1 mb-0"
                        style={{maxHeight: 160, overflow: 'auto'}}
                    >
                        {formatArguments(item.argumentsJson)}
                    </pre>
                </div>
            )}

            {isPending && (
                <div className="d-flex gap-2 mt-2">
                    <Button
                        color="success"
                        size="sm"
                        disabled={isSubmitting}
                        onClick={() => onDecision(true)}
                    >
                        Approve
                    </Button>
                    <Button
                        outline
                        color="danger"
                        size="sm"
                        disabled={isSubmitting}
                        onClick={() => onDecision(false)}
                    >
                        Deny
                    </Button>
                </div>
            )}
        </Alert>
    );
};

export default ApprovalRequestCard;
